import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Loader2, ArrowUp } from 'lucide-react';
import { Note } from '../../types/note';
import { api } from '../../services/api';

/**
 * Full-screen note reader with INCREMENTAL rendering:
 * - content fetched in 64KB chunks from /api/notes/:id/content
 * - each chunk is split into markdown block segments (blank-line boundary,
 *   code-fence aware) and rendered progressively
 * - IntersectionObserver on a bottom sentinel loads the next chunk
 * - only the visible portion exists in the React tree → multi-MB notes stay fast
 */
interface NoteFullViewProps {
    note: Note;
    onClose: () => void;
}

/**
 * Windowed markdown block list: all block texts live in memory (cheap), but
 * only blocks near the viewport are parsed/rendered. Keeps multi-MB notes
 * from exploding the React tree (measured: 3.1MB file crashed without this).
 */
const MarkdownBlockList: React.FC<{
    segments: string[];
    headerHeight: number;
    registerScroll: (fn: (scrollTop: number) => void) => void;
}> = ({ segments, headerHeight, registerScroll }) => {
    const EST_BLOCK_H = 120;
    const overscan = 6;

    const [scrollTop, setScrollTop] = useState(0);
    const [viewportH, setViewportH] = useState(() =>
        typeof window === 'undefined' ? 800 : window.visualViewport?.height ?? (window.innerHeight - headerHeight),
    );

    useEffect(() => {
        const onResize = () => setViewportH(window.visualViewport?.height ?? (window.innerHeight - headerHeight));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [headerHeight]);

    useEffect(() => {
        registerScroll(setScrollTop);
    }, [registerScroll]);

    const first = Math.max(0, Math.floor(scrollTop / EST_BLOCK_H) - overscan);
    const visibleCount = Math.ceil(viewportH / EST_BLOCK_H) + overscan * 2;
    const last = Math.min(segments.length, first + visibleCount);

    return (
        <>
            {/* spacer above the window */}
            <div style={{ height: first * EST_BLOCK_H }} aria-hidden />
            <div className="prose-sm space-y-4 text-sm text-gray-700 dark:text-gray-200 leading-relaxed
                [&_a]:text-blue-500 [&_a]:underline [&_a]:break-all [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h3]:text-lg [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_code]:bg-gray-100 dark:[&_code]:bg-gray-800 [&_code]:rounded [&_code]:px-1 [&_code]:text-[13px] [&_pre]:bg-gray-100 dark:[&_pre]:bg-gray-800 [&_pre]:rounded-lg [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_blockquote]:border-l-2 [&_blockquote]:border-blue-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-500 [&_table]:w-full [&_table]:text-xs [&_th]:border [&_th]:p-1.5 [&_th]:bg-gray-50 dark:[&_th]:bg-gray-800 [&_td]:border [&_td]:p-1.5 [&_hr]:border-gray-200 dark:[&_hr]:border-gray-700">
                {segments.slice(first, last).map((seg, i) => (
                    <ReactMarkdown
                        key={first + i}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            a: ({ href, children }) => (
                                <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                            ),
                        }}
                    >
                        {seg}
                    </ReactMarkdown>
                ))}
            </div>
            {/* spacer below the window */}
            <div style={{ height: (segments.length - last) * EST_BLOCK_H }} aria-hidden />
        </>
    );
};

const CHUNK_LEN = 65536;
const PREFETCH_THRESHOLD = 600; // px before sentinel triggers a fetch

export const NoteFullView: React.FC<NoteFullViewProps> = ({ note, onClose }) => {
    const [segments, setSegments] = useState<string[]>([]);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showTop, setShowTop] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollListeners = useRef(new Set<(top: number) => void>());
    const registerScroll = useCallback((fn: (top: number) => void) => {
        scrollListeners.current.add(fn);
        return () => scrollListeners.current.delete(fn);
    }, []);
    const fetching = useRef(false);
    const openFence = useRef(false); // chunk ended inside a ``` fence

    const isMarkdown = note.type === 'markdown';

    const loadMore = useCallback(async () => {
        if (fetching.current) return;
        if (total !== null && offset >= total) return;
        fetching.current = true;
        setLoading(true);
        try {
            const res = await api.getNoteContent(note.id, offset, CHUNK_LEN);
            setTotal(res.total);

            let text = res.chunk;
            // stitch with a previously unterminated code fence
            if (openFence.current) text = '```\n' + text;

            if (res.hasMore) {
                const fences = (text.match(/^```/gm) || []).length;
                openFence.current = fences % 2 === 1;
                if (openFence.current) {
                    // keep the fence open: append segments but re-open next time
                }
            } else {
                openFence.current = false;
            }

            // split at blank-line boundaries into renderable segments
            const parts = text.split(/\n\n+/).filter((p) => p.trim().length > 0);
            setSegments((prev) => [...prev, ...parts]);
            setOffset(res.end);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load note');
        } finally {
            fetching.current = false;
            setLoading(false);
        }
    }, [note.id, offset, total]);

    // initial load
    useEffect(() => {
        setSegments([]);
        setOffset(0);
        setTotal(null);
        openFence.current = false;
        loadMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [note.id]);

    // infinite scroll
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMore();
            },
            { rootMargin: `${PREFETCH_THRESHOLD}px 0px` },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [loadMore]);

    // Esc to close + scroll-to-top affordance
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        const c = containerRef.current;
        const onScroll = () => {
            const top = c?.scrollTop ?? 0;
            setShowTop(top > 1200);
            scrollListeners.current.forEach((fn) => fn(top));
        };
        c?.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('keydown', onKey);
            c?.removeEventListener('scroll', onScroll);
        };
    }, [onClose]);

    const progress = total ? Math.min(100, Math.round((offset / total) * 100)) : 0;
    const fmtSize = (b: number) =>
        b >= 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.round(b / 1024) + ' KB';

    return (
        <div className="fixed inset-0 z-50 bg-white dark:bg-gray-950 flex flex-col" role="dialog" aria-label={`Note: ${note.title}`}>
            {/* Header */}
            <div className="shrink-0 px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{note.title}</h2>
                    <p className="text-xs text-gray-400">
                        {note.type === 'markdown' ? 'Markdown' : note.type === 'url' ? 'Link' : 'Note'}
                        {total !== null && <> · {fmtSize(total)} · {progress}% loaded</>}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    aria-label="Close note"
                >
                    <X size={20} />
                </button>
            </div>

            {/* thin progress bar */}
            <div className="shrink-0 h-0.5 bg-gray-100 dark:bg-gray-800">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
            </div>

            {/* Content */}
            <div ref={containerRef} className="flex-1 overflow-y-auto overscroll-contain">
                <div className="max-w-3xl mx-auto px-5 py-6">
                    {error && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm">
                            {error}
                        </div>
                    )}

                    {isMarkdown ? (
                        <MarkdownBlockList
                            segments={segments}
                            headerHeight={112}
                            registerScroll={registerScroll}
                        />
                    ) : note.type === 'url' ? (
                        <a href={note.content ?? note.preview} target="_blank" rel="noopener noreferrer"
                           className="text-blue-500 underline break-all">
                            {note.content ?? note.preview}
                        </a>
                    ) : (
                        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                            {segments.join('\n\n')}
                        </pre>
                    )}

                    {/* loading + sentinel */}
                    <div ref={sentinelRef} className="h-24 flex items-center justify-center">
                        {loading && <Loader2 className="animate-spin text-blue-500" size={22} />}
                        {!loading && total !== null && offset >= total && (
                            <p className="text-xs text-gray-300">— end of note —</p>
                        )}
                    </div>
                </div>
            </div>

            {showTop && (
                <button
                    onClick={() => containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="fixed bottom-6 right-6 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                    aria-label="Scroll to top"
                >
                    <ArrowUp size={18} />
                </button>
            )}
        </div>
    );
};
